
import type {
  ProductDomainService,
  QueryRanking,
  QueryRankingConfigDTO,
  QueryRankingElementRankingDTO,
  QueryRankingHiddenElementDTO,
  QueryRankingPinnedElementDTO,
} from '@empathyco/playboard-core-domain'
import type { PropType, SetupContext } from 'vue'
import type { QueryRankingElementActionConfig } from '../../composables/use-query-ranking-element-actions-config'
import type { RegularFilter, RegularFilterSelectedValues, ToolsMessages } from '../../models'
import type { QueryRankingDomainService } from '../../services/interfaces/domain/query-ranking-domain.service'
import type { ExtraParamsRaw } from '../../x/adapter/types'
import type { EpbSelectOption } from '../shared/epb-select'
import {
  HiddenElementsAction,
  PinProductAction,
  QueryRankingElementRankingAction,
} from '@empathyco/playboard-core-domain'
import { computed, defineComponent, ref, watch } from 'vue'
import ConfigurationsSearchInput from '../../components/configurations/configurations-search-input.component.vue'
import DatePicker from '../../components/date-picker.component.vue'
import ToolingPreview from '../../components/tooling/tooling-preview.component.vue'
import XQuerySelect from '../../components/x/x-query-select.component.vue'
import { useX } from '../../composables/x/use-x'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { EndpointContextEnum } from '../../enums'
import { ScheduleUtils } from '../../utils/schedule.utils'

export default defineComponent({
  name: 'ToolingPreviewQueryRanking',
  components: {
    ConfigurationsSearchInput,
    DatePicker,
    ToolingPreview,
    XQuerySelect,
  },
  props: {
    readOnly: Boolean,
    messages: {
      type: Object as PropType<ToolsMessages['configurations']>,
      required: true,
    },
    filters: {
      type: Object as PropType<Record<string, RegularFilter>>,
      required: true,
    },
    endpointId: {
      type: String,
      default: 'search',
    },
    context: {
      type: String as PropType<EndpointContextEnum>,
      default: EndpointContextEnum.QUERY,
    },
    criteria: {
      type: Object as PropType<QueryRanking['criteria']>,
      required: true,
    },
    attributesRanking: {
      type: Array as PropType<Array<QueryRankingElementRankingDTO>>,
      required: true,
    },
    hiddenAttributes: {
      type: Array as PropType<Array<QueryRankingHiddenElementDTO>>,
      required: true,
    },
    productsRanking: {
      type: Array as PropType<Array<QueryRankingElementRankingDTO>>,
      required: true,
    },
    pinnedProducts: {
      type: Array as PropType<Array<QueryRankingPinnedElementDTO>>,
      required: true,
    },
    hiddenProducts: {
      type: Array as PropType<Array<QueryRankingHiddenElementDTO>>,
      required: true,
    },
  },
  emits: ['filtersSelectedValuesChange'],
  setup(props, ctx: SetupContext) {
    const x = useX()

    const productDomainService = CONTAINER.get<ProductDomainService>(TYPES.ProductDomainService)
    const qrDomainService = CONTAINER.get<QueryRankingDomainService>(
      TYPES.QueryRankingDomainService,
    )

    const dateFilter = ref(new Date())
    const selectedQuery = ref<string>()
    const productSearchQuery = ref<string>()

    const isSearchWithinBrowse = computed(
      () => props.context === EndpointContextEnum.FILTER && !!productSearchQuery.value,
    )

    const previewContext = computed(() =>
      isSearchWithinBrowse.value ? EndpointContextEnum.QUERY : props.context,
    )

    const queries = computed<Array<EpbSelectOption>>(() => {
      if (props.context === EndpointContextEnum.QUERY) {
        return props.criteria.queries.map(query => ({ value: query }))
      }

      if (props.criteria.browseField && props.criteria.browseValue) {
        return [
          {
            value: `${props.criteria.browseField}:${props.criteria.browseValue}`,
            label: props.criteria.browseValue,
          },
        ]
      }

      return []
    })

    const xQuery = computed(() => {
      if (!productSearchQuery.value) {
        return selectedQuery.value
      }

      if (props.context === EndpointContextEnum.QUERY) {
        return `${selectedQuery.value} ${productSearchQuery.value}`
      }

      return productSearchQuery.value
    })

    const extraParams = computed<ExtraParamsRaw>(() => {
      const getFilteredProductsRanking = <
        T extends QueryRankingConfigDTO & Pick<QueryRankingElementActionConfig, 'action'>,
      >(
        action: QueryRankingElementActionConfig['action'],
        elementRankings: Array<T>,
        date: Date,
      ): Array<T> =>
        elementRankings.filter(
          productRanking =>
            productRanking.action === action &&
            ScheduleUtils.isDateWithinSchedule(date, productRanking.schedule) &&
            productDomainService.isProductIdValid(productRanking.fieldValue),
        )
      const getFilteredAttributesRanking = (
        action: QueryRankingElementRankingDTO['action'],
        date: Date,
      ): Array<QueryRankingElementRankingDTO> =>
        props.attributesRanking.filter(
          attributeRanking =>
            attributeRanking.action === action &&
            ScheduleUtils.isDateWithinSchedule(date, attributeRanking.schedule),
        )

      const getFilteredHiddenAttributes = (
        action: QueryRankingHiddenElementDTO['action'],
        date: Date,
      ): Array<QueryRankingHiddenElementDTO> =>
        props.hiddenAttributes.filter(
          hiddenAttribute =>
            hiddenAttribute.action === action &&
            ScheduleUtils.isDateWithinSchedule(date, hiddenAttribute.schedule),
        )

      const params: ExtraParamsRaw = {
        queryRanking: {
          products: {
            fieldName: qrDomainService.getProductIdField(),
            boosted: getFilteredProductsRanking(
              QueryRankingElementRankingAction.BOOST,
              props.productsRanking,
              dateFilter.value,
            ),
            buried: getFilteredProductsRanking(
              QueryRankingElementRankingAction.BURY,
              props.productsRanking,
              dateFilter.value,
            ),
          },
          attributes: {
            boosted: getFilteredAttributesRanking(
              QueryRankingElementRankingAction.BOOST,
              dateFilter.value,
            ),
            buried: getFilteredAttributesRanking(
              QueryRankingElementRankingAction.BURY,
              dateFilter.value,
            ),
          },
        },
      }

      params.queryRanking.hiddenAttributes = getFilteredHiddenAttributes(
        HiddenElementsAction,
        dateFilter.value,
      )

      params.queryRanking.pinnedElements = getFilteredProductsRanking(
        PinProductAction,
        props.pinnedProducts,
        dateFilter.value,
      )

      params.queryRanking.hiddenProducts = getFilteredProductsRanking(
        HiddenElementsAction,
        props.hiddenProducts,
        dateFilter.value,
      )

      if (productSearchQuery.value) {
        return {
          filter:
            props.context === EndpointContextEnum.FILTER && selectedQuery.value
              ? [selectedQuery.value]
              : undefined,
          ...params,
        }
      }

      return params
    })

    const inputProps = {
      datePickerLabel: props.messages.fields.placeholders.scheduled,
      round: false,
    }

    const onProductSearchChange = (query: string): void => {
      productSearchQuery.value = query
    }

    const onSelectedQuery = (query: string): void => {
      selectedQuery.value = query
      productSearchQuery.value = undefined
    }

    const onPreviewFiltersSelectedValuesChange = (
      newPreviewFiltersSelectedValues: Record<string, RegularFilterSelectedValues>,
    ): void => {
      ctx.emit('filtersSelectedValuesChange', newPreviewFiltersSelectedValues)
    }

    const onDateFilterChange = (date?: Date): void => {
      dateFilter.value = date ?? new Date()
    }

    watch(xQuery, value => {
      if (value) {
        x.setQuery(value)
      }
    })

    return {
      baseClass: 'epb-tooling-preview-auery-ranking',
      dateFilter,
      isSearchWithinBrowse,
      previewContext,
      productSearchQuery,
      queries,
      extraParams,
      inputProps,
      onPreviewFiltersSelectedValuesChange,
      onDateFilterChange,
      onProductSearchChange,
      onSelectedQuery,
    }
  },
})
